import { beschriftung, t, dezimalzahl, anzahlwort } from './sprache'
import type { Sprachschluessel } from './sprache'
import type { Vorschlagsrichtung, Zielfeld } from './typen'
import type { Packagezuschnitt } from './Packagezuschnitt'

/**
 * Die Spaltenzuordnung einer Datenquelle — das, was der Anwender am Blatt entscheidet
 * und was der Importdienst liest (5.1 Abschnitt 3.7, R521–R525).
 *
 * **Reine Logik, ohne Bildschirm:** Was hier steht, lässt sich prüfen, ohne
 * einen Browser zu starten — der Zustand je Feld, die Zählung, der Satz.
 */

/** Wie ein Zielfeld belegt ist (R523, R524, R525). */
export type Feldbindung = {
  /** Die Spalte, nullbasiert. */
  spalte?: number
  /** Die Überschrift der Spalte beim Anlegen — die Erinnerung, an der der Wechsel erkannt wird. */
  kopf?: string
  /** Der feste Wert je Datei, aus dem Vorrat. */
  wert?: string
  /** Ausdrücklich entschieden: Diese Datei bringt das Feld nicht. */
  nicht?: boolean
}

/** Was die Datei liefert (R540): einen Stand („Saldo") oder die Bewegung der Periode („Verkehrszahl"). */
export type Lieferart = 'saldo' | 'verkehrszahl'

/**
 * Wie der Betrag entsteht (R580): aus einer Wertspalte, aus Soll und Haben oder
 * aus Wert und S/H-Kennzeichen.
 *
 * Die Betriebsart sagt, **wie** der Betrag entsteht — nicht, **was** er
 * bedeutet; das sagt die Lieferart (R540).
 */
export type Betragsart = 'wert' | 'sollhaben' | 'kennzeichen' | 'spiegel' | 'package'

/**
 * Die Bewegungsarten, die gerechnet werden und deshalb nie geliefert werden
 * dürfen (R739) — ihre Spalten tragen im Spiegel die Probe (R741).
 */
// **Bis zum 2026-10-04 stand hier 'BWA-AB'** — der Schlüssel aus Migration
// 0083, den 0093 in allen Datenquellen auf 'BWA-EB' umgeschrieben hat. Eine
// Anfangsbestandsspalte zählte damit als Buchungsspalte, und die Probemarke
// erschien nie.
export const BEWEGUNGSART_ANFANGSBESTAND = 'BWA-EB'
export const BEWEGUNGSART_BUCHWERT = 'BWA-SB'

export function istGerechneteBewegungsart(schluessel: string): boolean {
  return schluessel === BEWEGUNGSART_ANFANGSBESTAND || schluessel === BEWEGUNGSART_BUCHWERT
}

/** Die Felder, aus denen der Betrag entsteht, wenn er nicht in einer Wertspalte steht (R580). */
export const BETRAG_SOLL = 'betrag_soll'
export const BETRAG_HABEN = 'betrag_haben'
export const BETRAG_KENNZEICHEN = 'betrag_sh'

/** Der Platzhalter für die lokale Währung (R581) — kein ISO-Code, sondern eine Herkunft. */
export const WAEHRUNG_LOKAL = '@lokal'

/** Die wählbaren Trennungen einer Kontospalte, die Nummer und Name zusammen trägt (R579). */
export const KONTOTRENNUNGEN = [' ', '-', ':', '/', ';', '|'] as const

/**
 * Die Spaltenzuordnung, wie der Dienst sie hält.
 *
 * **Der Typ heißt weiter `Abbildung`, die Oberfläche sagt „Spaltenzuordnung".**
 * Am Bildschirm hieß dieselbe Sache bis zum 2026-09-17 mal „Abbildung", mal
 * „Die Zuordnung" — und „Zuordnung" trug zugleich die Kontenzuordnung. Seither
 * trennen zwei Wörter die zwei Sachen. Der Name hier bleibt, weil er in
 * `abbildungLesen`, `abbildungSchreiben` und im Feld `abbildungJson` des
 * Dienstes steht: Ein Wort am Bildschirm zu ändern kostet eine Zeile, ein Feld
 * über die Schnittstelle hinweg eine Migration.
 */
export type Abbildung = {
  trennzeichen: string
  dezimaltrennzeichen: string
  kopfzeilen: number
  fusszeilen: number
  blatt: string
  faktor: number
  vorzeichen: 1 | -1
  lieferart: Lieferart
  betragsart: Betragsart
  /** Wie die Kontospalte geteilt wird (R579) — leer, wenn sie nur die Nummer trägt. */
  kontotrennung: string
  /**
   * Woher die Periode kommt, wenn die Lieferung sie selbst mitbringt (R728).
   *
   * Leer heißt: Sie wird wie bisher beim Starten des Imports gestellt. Ein
   * Stapel läuft dann nicht — er stellt keine 24 Perioden von Hand.
   */
  periodenherkunft: '' | 'dateiname' | 'zelle'
  /** Das Muster für den Dateinamen — aus dem Vorrat, kein Freitext (R728). */
  periodenmuster: string
  /** Die Zelle, in der die Periode steht — `B4`, wie in der Tabelle abgelesen (R728). */
  periodenzelle: string
  felder: Record<string, Feldbindung>
  /**
   * Welche Wertspalte welche Bewegungsart trägt, wenn die Lieferung ein
   * Spiegel in Spaltenform ist (R740) — Spaltenstelle auf Schlüssel.
   *
   * Anfangs- und Endbestandsspalte stehen mit darin und werden trotzdem nie
   * gebucht: Sie tragen die Probe (R741).
   */
  spiegelspalten: Record<number, string>
  /**
   * Die Vorschläge zur Bewegungsart (5.1 Abschnitt 3.35, R871) — je Zeile
   * Konzernkonto, Richtung und Bewegungsart, alle drei gewählt.
   *
   * **Ein Vorschlag und kein Automatismus** (R873): Er wird bei jedem Import
   * gefragt und gilt ab dem nächsten.
   */
  vorschlaege: Bewegungsartvorschlag[]
  /**
   * Was eine Package-Datenquelle sich merkt (5.1 Abschnitt 3.37, R892). Der
   * Fragebogen der Package-Vorlagen schreibt ihn; dieser Bildschirm trägt ihn
   * nur durch, damit Speichern hier ihn nicht löscht.
   */
  zuschnitt?: Packagezuschnitt
}

/** Ein Vorschlag an der Datenquelle (R871). Leere Felder stehen nur, solange er angelegt wird. */
export type Bewegungsartvorschlag = {
  konto: string
  richtung: Vorschlagsrichtung
  bewegungsart: string
}


export type Feldzustand = 'zugeordnet' | 'nichtgeliefert' | 'offen'

export const LEERE_ABBILDUNG: Abbildung = {
  trennzeichen: ';',
  dezimaltrennzeichen: ',',
  kopfzeilen: 1,
  fusszeilen: 0,
  blatt: '',
  faktor: 1,
  vorzeichen: 1,
  lieferart: 'saldo',
  betragsart: 'wert',
  kontotrennung: '',
  periodenherkunft: '',
  periodenmuster: '',
  periodenzelle: '',
  felder: {},
  spiegelspalten: {},
  vorschlaege: [],
}

/** Die wählbaren Muster für den Dateinamen (R728) — dieselben wie im Dienst. */
export const PERIODENMUSTER = ['JJJJ-MM', 'JJJJMM', 'JJJJ_MM', 'MM.JJJJ', 'MM-JJJJ', 'MM_JJJJ'] as const


/**
 * Liest die gespeicherte Spaltenzuordnung — die neue Form mit einer Bindung je Feld
 * und die alte aus dem Spalten-Designer (nur ein Index je Feld).
 *
 * Was sich nicht lesen lässt, wird zur leeren Spaltenzuordnung: Das Blatt zeigt dann
 * alles als offen, statt einen Fehler zu zeigen, den der Anwender nicht
 * beheben kann.
 */
export function abbildungLesen(json: string | null | undefined): Abbildung {
  if (!json) {
    return { ...LEERE_ABBILDUNG, felder: {}, spiegelspalten: {}, vorschlaege: [] }
  }

  let roh: unknown
  try {
    roh = JSON.parse(json)
  } catch {
    return { ...LEERE_ABBILDUNG, felder: {}, spiegelspalten: {}, vorschlaege: [] }
  }

  if (typeof roh !== 'object' || roh === null) {
    return { ...LEERE_ABBILDUNG, felder: {}, spiegelspalten: {}, vorschlaege: [] }
  }

  const r = roh as Record<string, unknown>
  const felder: Record<string, Feldbindung> = {}

  if (typeof r.felder === 'object' && r.felder !== null) {
    for (const [name, b] of Object.entries(r.felder as Record<string, unknown>)) {
      if (typeof b !== 'object' || b === null) continue
      const bb = b as Record<string, unknown>
      const bindung: Feldbindung = {}
      if (typeof bb.spalte === 'number') bindung.spalte = bb.spalte
      if (typeof bb.kopf === 'string' && bb.kopf) bindung.kopf = bb.kopf
      if (typeof bb.wert === 'string' && bb.wert) bindung.wert = bb.wert
      if (bb.nicht === true) bindung.nicht = true
      felder[name] = bindung
    }
  } else if (typeof r.spalten === 'object' && r.spalten !== null) {
    for (const [name, i] of Object.entries(r.spalten as Record<string, unknown>)) {
      if (typeof i === 'number') felder[name] = { spalte: i }
    }
  }

  /*
    **Die Spaltenstelle ist der Schlüssel, die Bewegungsart der Wert** (R740).
    JSON kennt nur Zeichenketten als Namen; was sich nicht als Zahl liest,
    fällt weg, statt die ganze Zuordnung zu verwerfen — eine unlesbare Spalte
    ist eine offene Spalte.
  */
  const spiegelspalten: Record<number, string> = {}
  if (typeof r.spiegelspalten === 'object' && r.spiegelspalten !== null) {
    for (const [stelle, art] of Object.entries(r.spiegelspalten as Record<string, unknown>)) {
      const i = Number(stelle)
      if (Number.isInteger(i) && i >= 0 && typeof art === 'string' && art) spiegelspalten[i] = art
    }
  }

  /*
    **Gewählt und nicht getippt** (R871): Eine Zeile ohne Konto, ohne
    Bewegungsart oder mit einer Richtung außerhalb des Vorrats fällt weg — wie
    im Dienst, der dieselbe Zeile beim Lesen ebenso übergeht.
  */
  const vorschlaege: Bewegungsartvorschlag[] = []
  if (Array.isArray(r.vorschlaege)) {
    for (const v of r.vorschlaege as unknown[]) {
      if (typeof v !== 'object' || v === null) continue
      const vv = v as Record<string, unknown>
      if (
        typeof vv.konto === 'string' &&
        vv.konto &&
        (vv.richtung === 'zunahme' || vv.richtung === 'abnahme') &&
        typeof vv.bewegungsart === 'string' &&
        vv.bewegungsart
      ) {
        vorschlaege.push({ konto: vv.konto, richtung: vv.richtung, bewegungsart: vv.bewegungsart })
      }
    }
  }

  return {
    trennzeichen: typeof r.trennzeichen === 'string' ? r.trennzeichen : ';',
    dezimaltrennzeichen: typeof r.dezimaltrennzeichen === 'string' ? r.dezimaltrennzeichen : ',',
    kopfzeilen: typeof r.kopfzeilen === 'number' ? r.kopfzeilen : 1,
    fusszeilen: typeof r.fusszeilen === 'number' ? r.fusszeilen : 0,
    blatt: typeof r.blatt === 'string' ? r.blatt : '',
    faktor: typeof r.faktor === 'number' && r.faktor !== 0 ? r.faktor : 1,
    vorzeichen: typeof r.vorzeichen === 'number' && r.vorzeichen < 0 ? -1 : 1,
    lieferart: r.lieferart === 'verkehrszahl' ? 'verkehrszahl' : 'saldo',
    betragsart:
      r.betragsart === 'sollhaben' ||
      r.betragsart === 'kennzeichen' ||
      r.betragsart === 'spiegel' ||
      r.betragsart === 'package'
        ? r.betragsart
        : 'wert',
    // Nur aus dem Vorrat (R579): Was dort nicht steht, ist keine Trennung.
    kontotrennung:
      typeof r.kontotrennung === 'string' && (KONTOTRENNUNGEN as readonly string[]).includes(r.kontotrennung)
        ? r.kontotrennung
        : '',
    // Auch die Herkunft der Periode ist gewählt und nicht getippt (R728).
    periodenherkunft: r.periodenherkunft === 'dateiname' || r.periodenherkunft === 'zelle' ? r.periodenherkunft : '',
    periodenmuster:
      typeof r.periodenmuster === 'string' && (PERIODENMUSTER as readonly string[]).includes(r.periodenmuster)
        ? r.periodenmuster
        : '',
    periodenzelle: typeof r.periodenzelle === 'string' ? r.periodenzelle.toUpperCase() : '',
    felder,
    spiegelspalten,
    vorschlaege,
    ...(typeof r.zuschnitt === 'object' && r.zuschnitt !== null
      ? { zuschnitt: r.zuschnitt as Packagezuschnitt }
      : {}),
  }
}


/**
 * Legt die Werte ab Werk auf die Felder, die niemand gebunden hat (R726).
 *
 * **Vorausgewählt, nicht festgeschrieben:** Der Anwender sieht den Wert im Blatt,
 * kann ihn ändern oder das Feld als „nicht geliefert" abräumen; erst das
 * Speichern schreibt ihn in den Bestand. Eine Bindung, die schon da ist —
 * Spalte, fester Wert oder die ausdrückliche Entscheidung „nicht geliefert" —
 * bleibt unangetastet.
 *
 * **Welcher Wert das ist, entscheidet der Dienst am Zielfeld**, hier steht keine
 * Konstante: Bei der Bewertungsebene ist es die Rohdatenschicht, solange das
 * Haus genau eine führt.
 */
export function mitWertenAbWerk(a: Abbildung, zielfelder: Zielfeld[]): Abbildung {
  const felder = { ...a.felder }
  let ergaenzt = false

  for (const z of zielfelder) {
    if (!z.wertAbWerk) continue
    // Beim Package legt die Vorlage alles fest außer der Bewertungsebene (R1192).
    if (a.betragsart === 'package' && z.name !== 'ledger') continue
    if (feldzustand(felder[z.name]) !== 'offen') continue
    felder[z.name] = { ...felder[z.name], wert: z.wertAbWerk }
    ergaenzt = true
  }

  return ergaenzt ? { ...a, felder } : a
}

/**
 * Schreibt die Spaltenzuordnung so, wie der Dienst sie liest — ohne die Vorgaben, die
 * nichts sagen, und ohne Bindungen auf Felder, die es nicht mehr gibt
 * (Eröffnung, Soll, Haben aus einer alten Fassung), wenn die Zielfelder bekannt sind.
 */
export function abbildungSchreiben(a: Abbildung, zielfelder?: Zielfeld[]): string {
  // Die Betragsspalten sind Bindungen, keine Zielfelder (R580) — sie stehen
  // nicht in der Liste aus dem Modell und dürfen trotzdem nicht wegfallen.
  const bekannt = zielfelder
    ? new Set([...zielfelder.map((z) => z.name), BETRAG_SOLL, BETRAG_HABEN, BETRAG_KENNZEICHEN])
    : null
  const felder: Record<string, Feldbindung> = {}
  for (const [name, b] of Object.entries(a.felder)) {
    if (bekannt && !bekannt.has(name)) continue
    const bindung: Feldbindung = {}
    if (b.spalte !== undefined) bindung.spalte = b.spalte
    if (b.kopf) bindung.kopf = b.kopf
    if (b.wert) bindung.wert = b.wert
    if (b.nicht) bindung.nicht = true
    if (Object.keys(bindung).length > 0) felder[name] = bindung
  }

  return JSON.stringify({
    trennzeichen: a.trennzeichen,
    dezimaltrennzeichen: a.dezimaltrennzeichen,
    kopfzeilen: a.kopfzeilen,
    fusszeilen: a.fusszeilen,
    ...(a.blatt ? { blatt: a.blatt } : {}),
    ...(a.faktor !== 1 ? { faktor: a.faktor } : {}),
    ...(a.vorzeichen !== 1 ? { vorzeichen: a.vorzeichen } : {}),
    ...(a.lieferart === 'verkehrszahl' ? { lieferart: a.lieferart } : {}),
    ...(a.betragsart !== 'wert' ? { betragsart: a.betragsart } : {}),
    ...(a.kontotrennung ? { kontotrennung: a.kontotrennung } : {}),
    // Ohne Herkunft steht nichts da: Die Quelle sagt dann nichts über die
    // Periode, und der Einzelimport stellt sie wie bisher (R728).
    ...(a.periodenherkunft === 'dateiname'
      ? { periodenherkunft: 'dateiname', periodenmuster: a.periodenmuster || 'JJJJ-MM' }
      : {}),
    ...(a.periodenherkunft === 'zelle' && a.periodenzelle
      ? { periodenherkunft: 'zelle', periodenzelle: a.periodenzelle }
      : {}),
    // Die Spaltenzuordnung des Spiegels steht nur da, wo sie etwas bedeutet
    // (R740): Eine Quelle mit einer Wertspalte trägt sie nicht mit.
    ...(a.betragsart === 'spiegel' && Object.keys(a.spiegelspalten).length > 0
      ? { spiegelspalten: a.spiegelspalten }
      : {}),
    // Nur vollständige Vorschläge (R871): Eine Zeile, in der noch ein Feld
    // fehlt, ist im Anlegen und noch keiner.
    ...(a.vorschlaege.some(vollstaendigerVorschlag)
      ? { vorschlaege: a.vorschlaege.filter(vollstaendigerVorschlag) }
      : {}),
    // Der Zuschnitt reist nur mit, solange die Quelle ein Package liest
    // (R892): Wer sie auf eine Saldenliste umstellt, hat keinen mehr.
    ...(a.betragsart === 'package' && a.zuschnitt ? { zuschnitt: a.zuschnitt } : {}),
    felder,
  })

}

/** Ob ein Vorschlag alle drei Felder trägt (R871). */
export function vollstaendigerVorschlag(v: Bewegungsartvorschlag): boolean {
  return v.konto !== '' && v.bewegungsart !== ''
}

/** Der Zustand eines Feldes — drei, und der dritte ist der, wegen dem es die Zählung gibt (R523). */
export function feldzustand(b: Feldbindung | undefined): Feldzustand {
  if (b && (b.spalte !== undefined || (b.wert && b.wert.length > 0))) return 'zugeordnet'
  if (b?.nicht) return 'nichtgeliefert'
  return 'offen'
}

export type Zuordnungsstand = {
  zielfelder: number
  zugeordnet: number
  nichtGeliefert: number
  offen: number
  offeneFelder: string[]
  /** Ob Konto und Betrag eine Spalte und die Bewertungsebene eine Spalte oder einen Wert haben — ohne sie läuft die Quelle nicht (R313, R537). */
  pflichtfelder: boolean
}

/**
 * Ob sich aus den gebundenen Spalten ein Betrag lesen lässt (R580).
 *
 * Bei *Soll und Haben* werden beide Spalten verlangt: Eine Datei, die nur eine
 * Seite führt, ist die gewöhnliche Wertspalte.
 */
export function betragIstLesbar(a: Abbildung): boolean {
  if (a.betragsart === 'sollhaben') {
    return a.felder[BETRAG_SOLL]?.spalte !== undefined && a.felder[BETRAG_HABEN]?.spalte !== undefined
  }
  if (a.betragsart === 'kennzeichen') {
    return a.felder.betrag?.spalte !== undefined && a.felder[BETRAG_KENNZEICHEN]?.spalte !== undefined
  }
  // **Ein Spiegel braucht mindestens eine Spalte, aus der gebucht wird**
  // (R740/R741): Eine Datei, in der nur Anfangs- und Endbestand zugeordnet
  // sind, trägt die Probe und keine einzige Bewegung.
  if (a.betragsart === 'spiegel') {
    return buchungsspalten(a).length > 0
  }
  return a.felder.betrag?.spalte !== undefined
}

/** Die Spalten eines Spiegels, aus denen gebucht wird — ohne die der Probe (R741). */
export function buchungsspalten(a: Abbildung): [number, string][] {
  return Object.entries(a.spiegelspalten)
    .map(([stelle, art]): [number, string] => [Number(stelle), art])
    .filter(([, art]) => !istGerechneteBewegungsart(art))
}

/** Die Spalten eines Spiegels, die eine gerechnete Größe tragen — die Probe (R741). */
export function probespalten(a: Abbildung): [number, string][] {
  return Object.entries(a.spiegelspalten)
    .map(([stelle, art]): [number, string] => [Number(stelle), art])
    .filter(([, art]) => istGerechneteBewegungsart(art))
}

/** Zählt gegen die Zielfelder von heute — eine neue Dimension ist damit sofort offen (R522). */
export function zuordnungsstand(zielfelder: Zielfeld[], a: Abbildung): Zuordnungsstand {
  // **Beim Package zählt nur die Bewertungsebene** (R1192) — Blätter und
  // Spalten legt die Vorlage fest. Dieselbe Rechnung wie am Server.
  if (a.betragsart === 'package') {
    const ebene = feldzustand(a.felder.ledger) === 'zugeordnet'
    return {
      zielfelder: 1,
      zugeordnet: ebene ? 1 : 0,
      nichtGeliefert: 0,
      offen: ebene ? 0 : 1,
      offeneFelder: ebene ? [] : ['ledger'],
      pflichtfelder: ebene,
    }
  }

  const offeneFelder: string[] = []
  let zugeordnet = 0
  let nichtGeliefert = 0

  for (const z of zielfelder) {
    // Der Betrag zählt nach seiner Betriebsart (R580): Bei „Soll und Haben"
    // steht er in zwei anderen Spalten und ist trotzdem zugeordnet.
    if (z.name === 'betrag' && a.betragsart !== 'wert') {
      if (betragIstLesbar(a)) zugeordnet++
      else offeneFelder.push(z.name)
      continue
    }

    // Im Spiegel steht die Bewegungsart in den Spalten (R740) — sie ist dort
    // kein Feld, das einen Wert bekommt.
    if (z.name === 'bewegungsart' && a.betragsart === 'spiegel') {
      if (buchungsspalten(a).length > 0) zugeordnet++
      else offeneFelder.push(z.name)
      continue
    }

    const zustand = feldzustand(a.felder[z.name])
    if (zustand === 'zugeordnet') zugeordnet++
    else if (zustand === 'nichtgeliefert') nichtGeliefert++
    else offeneFelder.push(z.name)
  }

  // Pflicht heißt zugeordnet: Kontonummer und Betrag nur als Spalte
  // (nurSpalte), Bewertungsebene und Währung auch als fester Wert (R537, R581);
  // der Betrag zählt nach seiner Betriebsart (R580).
  const pflichtfelder = zielfelder
    .filter((z) => z.pflicht)
    .every((z) => {
      if (z.name === 'betrag') return betragIstLesbar(a)
      if (z.name === 'bewegungsart' && a.betragsart === 'spiegel') return buchungsspalten(a).length > 0
      const b = a.felder[z.name]
      return b?.spalte !== undefined || (!z.nurSpalte && !!b?.wert)
    })

  return {
    zielfelder: zielfelder.length,
    zugeordnet,
    nichtGeliefert,
    offen: offeneFelder.length,
    offeneFelder,
    pflichtfelder,
  }
}

/**
 * Die Gruppen des Blattes, in der Reihenfolge, in der man eine Datei liest —
 * das Konto zuerst, weil Nummer und Name zusammengehören (R578).
 */
export const GRUPPEN = ['konto', 'betrag', 'waehrung', 'kontierung', 'beleg', 'dimension'] as const
export type Gruppe = (typeof GRUPPEN)[number]

/**
 * Die Zeilen, aus denen der Betrag entsteht (R580).
 *
 * Bei *Soll und Haben* tritt der Betrag selbst zurück: Er steht in keiner
 * Spalte, sondern entsteht aus zweien. Die beiden Zeilen erben seine
 * Eigenschaften — Pflicht, und ein fester Wert ist keine Lieferung.
 */
export function betragsfelder(a: Abbildung, betrag: Zielfeld): Zielfeld[] {
  const wie = (name: string): Zielfeld => ({ ...betrag, name, namen: null, dimension: null })
  if (a.betragsart === 'sollhaben') return [wie(BETRAG_SOLL), wie(BETRAG_HABEN)]
  if (a.betragsart === 'kennzeichen') return [betrag, wie(BETRAG_KENNZEICHEN)]
  return [betrag]
}

export function gruppen(zielfelder: Zielfeld[], a?: Abbildung): { gruppe: Gruppe; felder: Zielfeld[] }[] {
  // **Im Spiegel tragen die Spalten Betrag und Bewegungsart** (R740): Beide
  // sind dort keine Zeile, die einen Wert bekommt — die Zuordnung steht an der
  // Spalte, und eine zweite Angabe daneben widerspräche ihr.
  const spiegel = a?.betragsart === 'spiegel'

  return GRUPPEN.map((gruppe) => ({
    gruppe,
    felder: zielfelder
      .filter((z) => z.gruppe === gruppe)
      .filter((z) => !(spiegel && (z.name === 'betrag' || z.name === 'bewegungsart')))
      .flatMap((z) => (z.name === 'betrag' && a ? betragsfelder(a, z) : [z])),
  })).filter((g) => g.felder.length > 0)
}

/**
 * Wie ein Wert aus dem Vorrat heißt — Code und Beschriftung, wie überall.
 *
 * **Der Platzhalter der lokalen Währung trägt keinen Code** (R581): „@lokal"
 * ist eine Herkunft und kein ISO-Code, und im Satz stünde er als Zeichenfolge
 * da, die niemandem etwas sagt.
 */
export function vorratsname(w: { schluessel: string; code: string; namen: Record<string, string> }): string {
  const text = beschriftung(w.namen)
  if (w.schluessel === WAEHRUNG_LOKAL) return text || w.code
  // **Ein Vorrat ohne Code trägt nur seinen Namen** (R678): Seit die Nummer in
  // die Bezeichnung gezogen ist, führen die Bewegungsarten keinen Code mehr —
  // und „ Startwert der Periode" mit führendem Leerzeichen wäre die Spur davon.
  if (!w.code) return text
  return text ? `${w.code} ${text}` : w.code
}

/** Dasselbe für den Satz: dort steht der Code allein — und für den Platzhalter sein Name. */
export function vorratskurz(w: { schluessel: string; code: string; namen: Record<string, string> }): string {
  return w.schluessel === WAEHRUNG_LOKAL ? beschriftung(w.namen) || w.code : w.code
}

/** Wie eine Trennung im Satz heißt (R579) — das Leerzeichen hat einen Namen, die Zeichen sich selbst. */
export function trennungsname(trennung: string): string {
  return trennung === ' ' ? t('quelle.trennung.leerzeichen') : t('quelle.trennung.zeichen', { zeichen: trennung })
}

/** Aus 0 wird A — Anwender sprechen über „Spalte F", nicht über „Spalte 5". */
export function spaltenname(stelle: number): string {
  let name = ''
  let rest = stelle
  do {
    name = String.fromCharCode(65 + (rest % 26)) + name
    rest = Math.floor(rest / 26) - 1
  } while (rest >= 0)

  return name
}

/** Eine Spalte der Datei, wie sie auf der Quellseite steht (R735). */
export type Spaltenzeile = {
  stelle: number
  /** A, B, C — wie der Anwender über die Spalte spricht. */
  buchstabe: string
  /** Die Überschriftzeile der Datei an dieser Stelle, leer wenn keine da ist. */
  kopf: string
  /** Der erste Datenwert darunter — er sagt mehr als jede Überschrift. */
  beispiel: string
  /** Die Zielfelder, die auf dieser Spalte liegen — mehrere sind erlaubt. */
  ziele: string[]
}

/**
 * Die Quellseite der Zuordnung: **jede** Spalte der Datei einmal, die
 * vergebenen mit ihrem Ziel daneben (R735).
 *
 * **Vergebene Spalten bleiben stehen.** Wer sie wegblendet, verschweigt, was
 * die Datei mitbringt — und Umhängen ginge nur noch über den Umweg *lösen*.
 * Die Zuordnung hängt am Zielfeld und nicht an der Spalte: Dieselbe Spalte
 * darf zwei Felder speisen (Währung und Transaktionswährung), deshalb ist
 * `ziele` eine Liste und kein einzelner Name.
 */
export function spaltenzeilen(
  spalten: number,
  ueberschrift: string[],
  beispielzeile: string[],
  abbildung: Abbildung,
  zielfelder: Zielfeld[],
): Spaltenzeile[] {
  // In der Reihenfolge des Blattes, damit zwei Ziele derselben Spalte immer
  // gleich herum dastehen (R735).
  const felder = gruppen(zielfelder, abbildung).flatMap((g) => g.felder)

  return Array.from({ length: spalten }, (_, stelle) => ({
    stelle,
    buchstabe: spaltenname(stelle),
    kopf: ueberschrift[stelle] ?? '',
    beispiel: beispielzeile[stelle] ?? '',
    ziele: felder.filter((z) => abbildung.felder[z.name]?.spalte === stelle).map(feldname),
  }))
}

/** Der Name eines Zielfelds — die Dimensionen beschriftet der Kunde, den Rest die Sprachdatei. */
export function feldname(z: Zielfeld): string {
  if (z.namen) {
    const eigener = beschriftung(z.namen)
    if (eigener) return eigener
  }

  return t(`quelle.feld.${z.name}` as Sprachschluessel)
}

/**
 * Die ganze Spaltenzuordnung in einem Satz — die Probe auf R136: Was sich nicht mehr
 * in einer Zeile sagen lässt, ist zu mächtig geworden.
 */
export function satzUeberQuelle(a: Abbildung, zielfelder: Zielfeld[], art: 'xlsx' | 'text' | null): string {
  const teile: string[] = []

  if (a.blatt) teile.push(t('quelle.satz.blatt', { blatt: a.blatt }))
  teile.push(t('quelle.satz.kopf', { kopf: anzahlwort(a.kopfzeilen), fuss: anzahlwort(a.fusszeilen) }))
  if (art === 'text' && a.trennzeichen !== ';') {
    teile.push(t('quelle.satz.trennzeichen', { zeichen: a.trennzeichen }))
  }

  const spalten: string[] = []
  const werte: string[] = []
  const nicht: string[] = []
  // **Der Betrag steht als seine Rechnung im Satz** (R580): „Betrag = D ‚Soll'
  // − E ‚Haben'" sagt in einer Zeile, was die Quelle tut — die Probe auf R136.
  const stellenname = (name: string): string | null => {
    const b = a.felder[name]
    return b?.spalte === undefined ? null : `${spaltenname(b.spalte)}${b.kopf ? ` „${b.kopf}"` : ''}`
  }

  for (const z of zielfelder) {
    if (z.name === 'betrag' && a.betragsart === 'sollhaben') {
      const soll = stellenname(BETRAG_SOLL)
      const haben = stellenname(BETRAG_HABEN)
      if (soll && haben) spalten.push(t('quelle.satz.sollhaben', { soll, haben }))
      continue
    }

    if (z.name === 'betrag' && a.betragsart === 'kennzeichen') {
      const wert = stellenname('betrag')
      const kennzeichen = stellenname(BETRAG_KENNZEICHEN)
      if (wert) spalten.push(`${feldname(z)} = ${wert}`)
      if (kennzeichen) spalten.push(t('quelle.satz.kennzeichen', { spalte: kennzeichen }))
      continue
    }

    const b = a.felder[z.name]
    if (!b) continue
    if (b.spalte !== undefined) {
      const stelle = `${spaltenname(b.spalte)}${b.kopf ? ` „${b.kopf}"` : ''}`
      // Die Trennung der Kontospalte steht dort, wo sie wirkt (R579).
      const zusatz =
        z.name === 'lokalnummer' && a.kontotrennung
          ? ' ' + t('quelle.satz.kontotrennung', { trennung: trennungsname(a.kontotrennung) })
          : ''
      spalten.push(`${feldname(z)} = ${stelle}${zusatz}`)
    } else if (b.wert) {
      const wert = z.werte.find((w) => w.schluessel === b.wert)
      werte.push(`${feldname(z)} = ${wert ? vorratskurz(wert) : b.wert}`)
    } else if (b.nicht) {
      nicht.push(feldname(z))
    }
  }

  if (spalten.length > 0) teile.push(spalten.join(', '))
  if (werte.length > 0) teile.push(werte.join(', '))
  if (nicht.length > 0) teile.push(t('quelle.satz.nicht', { felder: nicht.join(', ') }))
  if (a.faktor !== 1) teile.push(t('quelle.satz.faktor', { faktor: dezimalzahl(a.faktor) }))
  if (a.vorzeichen === -1) teile.push(t('quelle.satz.vorzeichen'))
  // Die Lieferart steht immer im Satz (R540): Was die Zahl ist, entscheidet,
  // was der Import daraus macht — das darf nicht in einer Vorgabe verschwinden.
  teile.push(t(a.lieferart === 'verkehrszahl' ? 'quelle.satz.verkehrszahl' : 'quelle.satz.saldo'))

  return teile.join(' · ')
}
